import React, { useState } from 'react';
import PageHeader from '@/components/PageHeader';
import EmptyState from '@/components/EmptyState';
import Avatar from '@/components/Avatar';
import ClientModal from '@/components/modals/ClientModal';
import { I } from '@/lib/icons';
import { useWorkspace, useClients, useInvoices } from '@/hooks/useData';
import { useToast } from '@/components/Toast';

export default function Klienti() {
  const { workspace } = useWorkspace();
  const { clients, loading, togglePin } = useClients(workspace?.id);
  const { invoices } = useInvoices(workspace?.id);
  const toast = useToast();
  const [modal, setModal] = useState({ open: false, client: null });
  const [q, setQ] = useState('');

  const list = clients.filter(c => c.name.toLowerCase().includes(q.toLowerCase()));
  const pinned = list.filter(c => c.is_pinned);
  const others = list.filter(c => !c.is_pinned);

  const handlePin = async (c) => {
    try { await togglePin(c); toast.success(c.is_pinned ? 'Odpipnuté' : 'Pripnuté'); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <>
      <PageHeader title="Klienti" meta={`${clients.length} celkom`} accent="pink"
        right={
          <button onClick={() => setModal({ open: true, client: null })}
            data-testid="new-client-btn" className="cd-btn cd-btn-primary">
            <I.plus /> Pridať klienta
          </button>
        } />

      {/* Search */}
      <div style={{ padding: '6px 32px 16px' }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '8px 12px', background: 'var(--bg-surface)',
          border: '1px solid var(--border)', borderRadius: 'var(--r-md)',
          maxWidth: 380,
        }}>
          <I.search />
          <input value={q} onChange={e => setQ(e.target.value)}
            placeholder="Hľadať klienta..."
            style={{
              flex: 1, border: 'none', outline: 'none', background: 'transparent',
              fontSize: 13, fontFamily: 'inherit', color: 'var(--text-primary)',
            }} />
        </div>
      </div>

      <div style={{ padding: '0 32px 32px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
            <span className="cd-spinner" /> Načítavam...
          </div>
        ) : list.length === 0 ? (
          <EmptyState icon="users" color="pink"
            title={clients.length === 0 ? 'Zatiaľ žiadni klienti' : 'Nič nesedí'}
            desc={clients.length === 0
              ? 'Pridaj prvého klienta. Pri vystavení FA si ho vyberáš z autocomplete.'
              : 'Skús iný hľadaný výraz.'}
            ctaLabel="Pridať klienta" onCta={() => setModal({ open: true, client: null })} />
        ) : (
          <>
            {pinned.length > 0 && (
              <div style={{ marginBottom: 18 }}>
                <div style={{
                  fontSize: 11, fontWeight: 700, color: 'var(--text-muted)',
                  textTransform: 'uppercase', letterSpacing: '0.07em',
                  marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6,
                }}><I.pin /> Pripnutí</div>
                <ClientList items={pinned} invoices={invoices} onClick={c => setModal({ open: true, client: c })} onPin={handlePin} />
              </div>
            )}
            <ClientList items={others} invoices={invoices} onClick={c => setModal({ open: true, client: c })} onPin={handlePin} />
          </>
        )}
      </div>

      <ClientModal
        open={modal.open}
        client={modal.client}
        onClose={() => setModal({ open: false, client: null })}
      />
    </>
  );
}

function ClientList({ items, invoices, onClick, onPin }) {
  if (items.length === 0) return null;
  return (
    <div style={{
      background: 'var(--bg-surface)', border: '1px solid var(--border)',
      borderRadius: 'var(--r-lg)', overflow: 'hidden',
    }}>
      {items.map((c, i) => {
        const count = invoices.filter(x => x.client_id === c.id).length;
        return (
          <div key={c.id} data-testid={`client-row-${c.id}`}
            onClick={() => onClick(c)} style={{
              display: 'flex', alignItems: 'center', gap: 14,
              padding: '12px 16px',
              borderBottom: i < items.length - 1 ? '1px solid var(--border)' : 'none',
              cursor: 'pointer',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            <Avatar name={c.name} size={40} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13.5, fontWeight: 600, marginBottom: 2 }}>{c.name}</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
                {c.ico ? `IČO ${c.ico}` : 'bez IČO'}
                {c.address && ` · ${c.address}`}
                {` · ${count} faktúr`}
              </div>
            </div>
            <button onClick={(e) => { e.stopPropagation(); onPin(c); }}
              data-testid={`pin-${c.id}`}
              style={{
                background: c.is_pinned ? 'var(--sec-orange-soft)' : 'transparent',
                border: 'none', color: c.is_pinned ? 'var(--sec-orange)' : 'var(--text-muted)',
                padding: 8, borderRadius: 'var(--r-full)', cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center',
              }}>
              {c.is_pinned ? <I.pin /> : <I.pinO />}
            </button>
            <span style={{ color: 'var(--text-muted)' }}><I.chevR /></span>
          </div>
        );
      })}
    </div>
  );
}
